import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Grid,
    Drawer
} from "@mui/material";
import { CONFIG } from "../../utils/config";
import requestHandler from "../../utils/requestHandler";

const ResetPassword: React.FC = () => {
    const [password, setPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");

    const [showRedirect, setShowRedirect] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [success, setSuccess] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);

    const [searchParams] = useSearchParams();

    // Extract token from URL
    const token = searchParams.get("token") || undefined;

    const logoSidebarWidth = 460

    const logoSidebar = (
        <Grid item sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            width: { sm: logoSidebarWidth },
            backgroundColor: 'black'
        }}>
            <img
                src='/movius-logo-digital-stacked-reversed.png'
                alt='Logo'
                style={{ width: '60%', objectFit: 'scale-down' }}
            />
        </Grid>
    )

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate inputs
        if (!password || !confirmPassword) {
            setError("Both fields are required.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setError("");
        setSuccess("");
        setLoading(true);

        try {
            const { RESET_PASSWORD } = CONFIG.ACCOUNTS;

            if (!token) {
                setError('Reset password token is missing.')
                return;
            }

            const result = await requestHandler('POST', RESET_PASSWORD, false, false, {
                password
            }, undefined, token);

            if (!result.hasError) {
                setSuccess('Password reset successfully!')
            } else if (result.status === 401) {
                setShowRedirect(true)
            } else {
                setError(result.message)
            }
        }
        catch (err) {
            setError("An error occurred while resetting the password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box style={{ height: '100vh' }}>

            {/* Render drawer for screen sizes: xs: 'none', sm: 'none', md: 'block' */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', sm: 'none', md: 'block' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: logoSidebarWidth, bgcolor: '#1e1d31' },
                }}
                open
            >
                {logoSidebar}
            </Drawer>

            {/* Render login form container */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    height: '100vh',
                    backgroundColor: '#fff5f4',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    ml: { md: `${logoSidebarWidth}px` }
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ minHeight: "100vh", padding: 2 }}
                >
                    <Typography variant="h4" gutterBottom>
                        Reset Password
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {showRedirect && <p>Invalid token or token expired. {' '} <Link to='/accounts/forgot-password'>Get new token</Link></p>}

                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                    {success && <Link to='/Login'>Go to Login</Link>}


                    <Box
                        component="form"
                        onSubmit={handleResetPassword}
                        sx={{ maxWidth: 400, width: "100%" }}
                    >
                        <TextField
                            label="New Password"
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            autoComplete="off"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <TextField
                            label="Confirm Password"
                            type="password"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            autoComplete="off"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={loading}
                            sx={{ mt: 2 }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ResetPassword;
